import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { FormattedContent } from '../utils/textUtils';
import PostDetailModal from '../components/PostDetailModal';
import { enhanceImageUrl } from '../utils/imageEnhancer';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Single source of truth: the named page (/username). The MY LIFE nav button lands here.
// Owner sees the full board + inline privacy toggles + edit controls; visitors see the public subset.
const MyLife = () => {
  const { username: routeUsername } = useParams();
  const { user, token, isAuthenticated, refreshUser } = useAuth();
  const navigate = useNavigate();

  const isOwner = Boolean(
    isAuthenticated && user && routeUsername &&
    user.username && user.username.toLowerCase() === routeUsername.toLowerCase()
  );
  const isPublicView = Boolean(routeUsername) && !isOwner;

  const [profile, setProfile] = useState(null);
  const [affirmationCards, setAffirmationCards] = useState([]); // [{ text, images: [...] }]
  const [privacy, setPrivacy] = useState([]); // owner only: per-affirmation private flags
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingToggle, setSavingToggle] = useState(null);
  const [expandedImage, setExpandedImage] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);

  // Legacy /my-life → redirect to the user's named page
  useEffect(() => {
    if (!routeUsername && isAuthenticated && user?.username) {
      navigate(`/${user.username}`, { replace: true });
    }
  }, [routeUsername, isAuthenticated, user, navigate]);

  useEffect(() => {
    if (isOwner) loadOwner();
    else if (isPublicView) loadPublic();
    // eslint-disable-next-line
  }, [routeUsername, isOwner, isPublicView, user, token]);

  const loadPublic = async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await axios.get(`${API}/public/profile/${encodeURIComponent(routeUsername)}`);
      setProfile(pickProfile(data));
      setAffirmationCards((data.affirmations || []).map((a) => ({ text: a.text, images: a.image ? [a.image] : [] })));
      setPosts(data.posts || []);
    } catch (err) {
      setError(err.response?.status === 403 ? 'private' : 'notfound');
    } finally {
      setLoading(false);
    }
  };

  const loadOwner = async () => {
    try {
      setLoading(true);
      setProfile(pickProfile(user));
      const affirmations = user.affirmations || [];
      const images = user.affirmation_images || [];
      setAffirmationCards(affirmations.map((text, i) => ({
        text,
        images: [0, 1, 2, 3].map((t) => images[i * 4 + t]).filter(Boolean),
      })));
      const priv = affirmations.map((_, i) => (i === 0 ? false : Boolean((user.affirmation_privacy || [])[i])));
      setPrivacy(priv);

      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get(`${API}/users/me/posts`, { headers });
      setPosts(res.data.posts || []);
    } catch (err) {
      console.error('Error loading My Life:', err);
    } finally {
      setLoading(false);
    }
  };

  const pickProfile = (d) => ({
    username: d.username,
    first_name: d.first_name,
    last_name: d.last_name,
    bio: d.bio,
    avatar: d.avatar,
    location: d.location,
    website: d.website,
    facebook_profile: d.facebook_profile,
    twitter_profile: d.twitter_profile,
    linkedin_profile: d.linkedin_profile,
    instagram_profile: d.instagram_profile,
  });

  const toggleAffirmationPublic = async (index) => {
    if (index === 0 || savingToggle !== null) return; // first is always public
    const updated = [...privacy];
    while (updated.length < affirmationCards.length) updated.push(false);
    updated[index] = !updated[index];
    setPrivacy(updated);
    setSavingToggle(index);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      await axios.put(`${API}/users/profile`, { affirmation_privacy: updated }, { headers });
      await refreshUser();
    } catch (err) {
      console.error('Failed to update affirmation privacy:', err);
      setPrivacy(privacy); // revert
    } finally {
      setSavingToggle(null);
    }
  };

  const displayName = profile
    ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || profile.username
    : '';
  const initials = (displayName || routeUsername || '?').slice(0, 2).toUpperCase();

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 flex items-center justify-center" data-testid="mylife-loading">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-300 border-t-purple-600 rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-purple-700 font-medium">Loading personal space...</p>
        </div>
      </div>
    );
  }

  if (isPublicView && error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 flex items-center justify-center px-6" data-testid="public-profile-error">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-semibold text-purple-800 mb-3">
            {error === 'private' ? 'This page is private' : 'Member not found'}
          </h2>
          <p className="text-purple-600 mb-6">
            {error === 'private'
              ? `${routeUsername} has chosen to keep their My Life page private.`
              : `We couldn't find a member named "${routeUsername}".`}
          </p>
          <Link to="/" className="inline-block px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700 transition-colors" data-testid="error-home-link">
            Explore Modern Human Being
          </Link>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-purple-800 mb-4">Please log in to view your personal space</h2>
          <button onClick={() => navigate('/login')} className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700">Go to Login</button>
        </div>
      </div>
    );
  }

  const hasSocial = profile.website || profile.facebook_profile || profile.twitter_profile || profile.linkedin_profile || profile.instagram_profile;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100" data-testid={isOwner ? 'mylife-page' : 'public-profile-page'}>
      <div className={`max-w-6xl mx-auto p-6 ${isPublicView && !isAuthenticated ? 'pb-28' : ''}`}>

        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-purple-800 mb-2" data-testid="public-profile-name">{displayName}</h1>
          <p className="text-purple-600">{isOwner ? 'Your personal tune-up center' : 'A Modern Human Being'}</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Left Column - Affirmations & Vision Board (1/3) */}
          <div className="lg:col-span-1">
            <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-6 shadow-xl" data-testid="public-profile-affirmations">
              <h2 className="text-2xl font-semibold text-purple-800 mb-2 flex items-center">
                <span className="mr-2">✨</span>
                Affirmations & Vision Board
              </h2>
              {isOwner && (
                <p className="text-xs text-purple-500 mb-4">
                  Toggle each affirmation Public to show it on your page. The first is always public.
                </p>
              )}

              {affirmationCards.length > 0 ? (
                <div className="space-y-6">
                  {affirmationCards.map((card, index) => (
                    <div key={index} className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-200" data-testid={`public-affirmation-${index}`}>
                      {isOwner && (
                        <div className="flex justify-end mb-2">
                          {index === 0 ? (
                            <span className="px-3 py-1 text-xs font-medium rounded-full bg-purple-100 text-purple-700" data-testid={`affirmation-lock-${index}`}>
                              🔒 Always Public
                            </span>
                          ) : (
                            <button
                              onClick={() => toggleAffirmationPublic(index)}
                              disabled={savingToggle !== null}
                              data-testid={`affirmation-toggle-${index}`}
                              className={`px-3 py-1 text-xs font-medium rounded-full transition-colors disabled:opacity-50 ${
                                privacy[index] ? 'bg-gray-200 text-gray-600 hover:bg-gray-300' : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200'
                              }`}
                            >
                              {privacy[index] ? 'Private' : 'Public'}
                            </button>
                          )}
                        </div>
                      )}
                      <p className="text-purple-800 font-medium mb-3">{card.text}</p>
                      {isOwner ? (
                        <div className="grid grid-cols-2 gap-2">
                          {[0, 1, 2, 3].map((t) => {
                            const image = card.images[t];
                            return image ? (
                              <img
                                key={t}
                                src={enhanceImageUrl(image)}
                                alt={`Vision ${index + 1}-${t + 1}`}
                                className="w-full h-40 object-cover rounded-lg border border-purple-300 cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => setExpandedImage(enhanceImageUrl(image))}
                              />
                            ) : (
                              <div key={t} className="w-full h-40 bg-purple-100 rounded-lg border border-purple-200 flex items-center justify-center">
                                <span className="text-purple-400 text-xs">No image</span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        card.images[0] && (
                          <img
                            src={enhanceImageUrl(card.images[0])}
                            alt={`Vision ${index + 1}`}
                            className="w-full h-40 object-cover rounded-lg border border-purple-300 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={() => setExpandedImage(enhanceImageUrl(card.images[0]))}
                          />
                        )
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-purple-600 mb-4">{isOwner ? 'Create your personal affirmations to get started' : 'No public affirmations shared yet.'}</p>
                  {isOwner && (
                    <button onClick={() => navigate('/profile')} className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700">Add Affirmations</button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Profile & Posts (2/3) */}
          <div className="lg:col-span-2 space-y-8">

            {/* Profile Information */}
            <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-6 shadow-xl" data-testid="public-profile-about">
              <div className="flex items-center space-x-4 mb-6">
                <div className="w-16 h-16 rounded-full overflow-hidden bg-purple-200 flex items-center justify-center shrink-0" data-testid="public-profile-avatar">
                  {profile.avatar ? (
                    <img src={enhanceImageUrl(profile.avatar)} alt="Avatar" className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-purple-600 text-xl font-semibold">{initials}</span>
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold text-purple-800">{displayName}</h3>
                  <p className="text-purple-600" data-testid="public-profile-username">@{profile.username}</p>
                  {profile.location && (
                    <p className="text-purple-500 text-sm" data-testid="public-profile-location">📍 {profile.location}</p>
                  )}
                </div>
                {isOwner && (
                  <button onClick={() => navigate('/profile')} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">Edit Profile</button>
                )}
              </div>

              {profile.bio && (
                <div className="mb-6">
                  <p className="text-purple-700" data-testid="public-profile-bio">{profile.bio}</p>
                </div>
              )}

              {hasSocial && (
                <div className={isOwner ? 'mb-6' : ''}>
                  <h3 className="text-lg font-semibold text-purple-800 mb-3">🔗 Connect</h3>
                  <div className="flex flex-wrap gap-2">
                    {profile.website && (
                      <a href={profile.website} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-2 bg-green-100 text-green-800 rounded-lg hover:bg-green-200 transition-colors text-sm">🌐 Website</a>
                    )}
                    {profile.facebook_profile && (
                      <a href={profile.facebook_profile} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-800 rounded-lg hover:bg-blue-200 transition-colors text-sm">📘 Facebook</a>
                    )}
                    {profile.twitter_profile && (
                      <a href={profile.twitter_profile} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-2 bg-sky-100 text-sky-800 rounded-lg hover:bg-sky-200 transition-colors text-sm">🐦 Twitter</a>
                    )}
                    {profile.linkedin_profile && (
                      <a href={profile.linkedin_profile} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-2 bg-blue-100 text-blue-900 rounded-lg hover:bg-blue-200 transition-colors text-sm">💼 LinkedIn</a>
                    )}
                    {profile.instagram_profile && (
                      <a href={profile.instagram_profile} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-2 bg-pink-100 text-pink-800 rounded-lg hover:bg-pink-200 transition-colors text-sm">📷 Instagram</a>
                    )}
                  </div>
                </div>
              )}

              {isOwner && (
                <div className="flex justify-center mt-2">
                  <button onClick={() => navigate('/create-post')} className="px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-xl font-medium hover:from-purple-700 hover:to-pink-700 transform hover:scale-105 transition-all">Say Something</button>
                </div>
              )}
            </div>

            {/* Posts Section */}
            <div className="bg-white/80 backdrop-blur-lg rounded-3xl p-6 shadow-xl" data-testid="public-profile-posts">
              <h2 className="text-2xl font-semibold text-purple-800 mb-6 flex items-center">
                <span className="mr-2">📝</span>
                {isOwner ? 'Your Conscious Contributions' : 'Conscious Contributions'}
              </h2>

              {posts.length > 0 ? (
                <div className="space-y-4">
                  {posts.map((post) => (
                    <div key={post.id} className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl border border-purple-200 hover:shadow-md transition-shadow" data-testid={`public-post-${post.id}`}>
                      <div className="flex items-start space-x-4">
                        {post.image && (
                          <img
                            src={enhanceImageUrl(post.image)}
                            alt="Post thumbnail"
                            className="w-20 h-20 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity shrink-0"
                            onClick={() => (isOwner ? setSelectedPost(post) : setExpandedImage(enhanceImageUrl(post.image)))}
                          />
                        )}
                        <div className="flex-1 min-w-0">
                          {post.title && (
                            <h3 className={`font-semibold text-purple-800 mb-2 ${isOwner ? 'cursor-pointer hover:text-purple-600' : ''}`} onClick={() => isOwner && setSelectedPost(post)}>
                              {post.title}
                            </h3>
                          )}
                          <div className="text-purple-700 mb-3 line-clamp-3">
                            <FormattedContent content={post.content} maxLines={3} />
                          </div>
                          <p className="text-purple-500 text-sm">{post.created_at ? new Date(post.created_at).toLocaleDateString() : ''}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-purple-600 mb-4">{isOwner ? "You haven't shared any posts yet" : 'No public posts yet.'}</p>
                  {isOwner && (
                    <button onClick={() => navigate('/create-post')} className="px-6 py-3 bg-purple-600 text-white rounded-xl hover:bg-purple-700">Share Your First Post</button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Join CTA for logged-out surfers (visitor view only) */}
      {isPublicView && !isAuthenticated && (
        <div className="fixed bottom-0 left-0 right-0 bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-2xl z-50" data-testid="public-profile-join-cta">
          <div className="max-w-6xl mx-auto px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="font-medium text-center sm:text-left">Inspired by {displayName}? Begin your own conscious journey.</p>
            <div className="flex gap-3">
              <button onClick={() => navigate('/login')} className="px-4 py-2 rounded-xl bg-white/20 hover:bg-white/30 transition-colors font-medium" data-testid="cta-login-btn">Log In</button>
              <button onClick={() => navigate('/register')} className="px-5 py-2 rounded-xl bg-white text-purple-700 hover:bg-purple-50 transition-colors font-semibold" data-testid="cta-join-btn">Join MHB</button>
            </div>
          </div>
        </div>
      )}

      {/* Image lightbox */}
      {expandedImage && (
        <div className="fixed inset-0 bg-black/80 z-[60] flex items-center justify-center p-4" onClick={() => setExpandedImage(null)} data-testid="public-profile-lightbox">
          <img src={expandedImage} alt="expanded" className="max-w-4xl max-h-full rounded-lg object-contain" />
        </div>
      )}

      {/* Post Detail Modal (owner only) */}
      {isOwner && selectedPost && (
        <PostDetailModal post={selectedPost} onClose={() => setSelectedPost(null)} currentUser={user} />
      )}
    </div>
  );
};

export default MyLife;
